import { computed, unref } from 'vue'
import DOMPurify from 'dompurify'

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'u', 's',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ol',
    'ul', 'li', 'a', 'span'
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'class', 'data-list',
    'data-checked'
  ],
}

// DOMPurify no valida el contenido de "style" ni restringe los valores de "class":
// un class="ql-align-center" legítimo del editor Quill es indistinguible, para
// DOMPurify, de un class que reutilice una utilidad de Tailwind ya compilada en el
// bundle (ej. una clase con position:fixed) para montar un overlay de phishing.
// Por eso no se permite "style" y "class" se filtra a solo clases de Quill (ql-*).
let hookRegistered = false

// Se registra de forma perezosa (al primer sanitize real) en vez de como efecto
// colateral de importación, y con un flag propio del módulo en vez de una
// propiedad mutada sobre el singleton de DOMPurify: así, si este archivo se
// recarga por HMR en dev, el flag vuelve a false junto con el resto del módulo
// y el hook se re-registra con la lógica editada, en lugar de quedar pegado a
// una versión vieja que nunca se actualiza.
function ensureRelHook() {
  if (hookRegistered) return

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer')
    }

    if (node.hasAttribute('class')) {
      const safeClasses = node.getAttribute('class')
        .split(/\s+/)
        .filter(c => c.startsWith('ql-'))
      if (safeClasses.length) {
        node.setAttribute('class', safeClasses.join(' '))
      } else {
        node.removeAttribute('class')
      }
    }
  })

  hookRegistered = true
}

/**
 * Composable para sanitizar HTML de forma reutilizable (Composition API)
 * @param {Ref<string> | string} htmlString - String o Ref con el HTML a sanitizar
 * @returns {import('vue').ComputedRef<string>} - Computed con el HTML sanitizado
 *
 * @example
 * // Composition API con script setup
 * const instructions = ref('<p>Hello</p>')
 * const sanitized = useSanitizedHtml(instructions)
 *
 * <template>
 *   <div v-html="sanitized"></div>
 * </template>
 */
export function useSanitizedHtml(htmlString) {
  ensureRelHook()
  return computed(() => DOMPurify.sanitize(unref(htmlString) || '', SANITIZE_CONFIG))
}

/**
 * Función helper para sanitizar HTML (compatible con Options API)
 * @param {string} htmlString - HTML a sanitizar
 * @returns {string} - HTML sanitizado
 *
 * @example
 * // Options API
 * methods: {
 *   getSanitized() {
 *     return sanitizeHtmlString(this.content)
 *   }
 * }
 *
 * // Computed en Options API
 * computed: {
 *   sanitizedContent() {
 *     return sanitizeHtmlString(this.content)
 *   }
 * }
 */
export function sanitizeHtmlString(htmlString) {
  ensureRelHook()
  return DOMPurify.sanitize(htmlString || '', SANITIZE_CONFIG)
}

/**
 * Sanitiza el content de un mensaje de flash (message.content), devolviendo ''
 * cuando no hay mensaje. Reutilizable en Options API: computed: { sanitizedMessage }.
 * @param {{ content: string } | null | undefined} message
 * @returns {string}
 */
export function sanitizeMessageContent(message) {
  return message ? sanitizeHtmlString(message.content) : ''
}
